import React, { createContext, useContext, useState, useEffect } from 'react';
import { setSecureItem, getSecureItem, removeSecureItem } from '../utils/secureStorage';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Theme state - always start in the dark (brand) theme. The UI has no theme switcher, so a
  // stale localStorage 'theme' = 'light' (e.g. left by another app on the same origin) must not
  // strip `theme-dark` — templates like classic rely on it to override their `bg-white` surfaces.
  const [isDarkTheme, setIsDarkTheme] = useState(true);

  // Auth state - Load from secure storage
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setIsLoadingUser] = useState(true);

  // First time visitor state - Load from localStorage
  const [isFirstTime, setIsFirstTime] = useState(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    return !hasVisited; // true if user hasn't visited before
  });

  // Cart state - Load from secure storage
  const [cart, setCart] = useState([]);
  const [isLoadingCart, setIsLoadingCart] = useState(true);

  // Wishlist state - Load from secure storage
  const [wishlist, setWishlist] = useState([]);
  const [isLoadingWishlist, setIsLoadingWishlist] = useState(true);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Load user from secure storage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const savedUser = await getSecureItem('user');
        if (savedUser) {
          setUser(savedUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Failed to load user from secure storage:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };
    loadUser();
  }, []);

  // Load cart from secure storage on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        const savedCart = await getSecureItem('cart');
        if (savedCart) {
          setCart(savedCart);
        }
      } catch (error) {
        console.error('Failed to load cart from secure storage:', error);
      } finally {
        setIsLoadingCart(false);
      }
    };
    loadCart();
  }, []);

  // Load wishlist from secure storage on mount
  useEffect(() => {
    const loadWishlist = async () => {
      try {
        const savedWishlist = await getSecureItem('wishlist');
        if (savedWishlist) {
          setWishlist(savedWishlist);
        }
      } catch (error) {
        console.error('Failed to load wishlist from secure storage:', error);
      } finally {
        setIsLoadingWishlist(false);
      }
    };
    loadWishlist();
  }, []);

  // Set initial theme on mount
  useEffect(() => {
    // Ensure dark theme is applied on initial load
    if (isDarkTheme) {
      document.body.classList.add('theme-dark');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set hasVisited in localStorage but keep session behavior until explicitly changed
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    if (!hasVisited) {
      console.log('First time visitor - setting hasVisited for future sessions');
      localStorage.setItem('hasVisited', 'true');
    }
  }, []);

  // Sync isFirstTime state with localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const hasVisited = localStorage.getItem('hasVisited');
      setIsFirstTime(!hasVisited);
    };

    // Listen for storage changes from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update theme in DOM and localStorage
  useEffect(() => {
    if (isDarkTheme) {
      document.body.classList.add('theme-dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.classList.remove('theme-dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkTheme]);

  // Update cart in secure storage
  useEffect(() => {
    if (!isLoadingCart) {
      setSecureItem('cart', cart).catch((error) => {
        console.error('Failed to save cart to secure storage:', error);
      });
    }
  }, [cart, isLoadingCart]);

  // Update wishlist in secure storage
  useEffect(() => {
    if (!isLoadingWishlist) {
      setSecureItem('wishlist', wishlist).catch((error) => {
        console.error('Failed to save wishlist to secure storage:', error);
      });
    }
  }, [wishlist, isLoadingWishlist]);

  // Theme functions
  const toggleTheme = () => {
    setIsDarkTheme((prev) => !prev);
  };

  // Auth functions
  const login = async (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    try {
      await setSecureItem('user', userData);
    } catch (error) {
      console.error('Failed to save user to secure storage:', error);
    }
  };

  const logout = async () => {
    setUser(null);
    setIsAuthenticated(false);
    try {
      await removeSecureItem('user');
    } catch (error) {
      console.error('Failed to remove user from secure storage:', error);
    }
  };

  // Cart functions
  const addToCart = (product) => {
    setCart((prev) => {
      const existingItem = prev.find((item) => item.id === product.id);
      if (existingItem) {
        return prev.map((item) => (item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item));
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) => prev.map((item) => (item.id === productId ? { ...item, quantity } : item)));
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Wishlist functions
  const addToWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((item) => item.id === product.id);
      if (exists) return prev;
      return [...prev, product];
    });
  };

  const removeFromWishlist = (productId) => {
    setWishlist((prev) => prev.filter((item) => item.id !== productId));
  };

  const isInWishlist = (productId) => {
    return wishlist.some((item) => item.id === productId);
  };

  // Sidebar functions
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Notification functions
  const addNotification = (notification) => {
    setNotifications((prev) => [...prev, { ...notification, id: Date.now() }]);
  };

  const removeNotification = (notificationId) => {
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  // First time visitor functions
  const markAsReturningUser = () => {
    console.log('markAsReturningUser called');
    localStorage.setItem('hasVisited', 'true');
    setIsFirstTime(false);
  };

  const resetFirstTimeStatus = () => {
    console.log('resetFirstTimeStatus called');
    localStorage.removeItem('hasVisited');
    setIsFirstTime(true);
  };

  const value = {
    // Theme
    isDarkTheme,
    toggleTheme,

    // Auth
    user,
    isAuthenticated,
    login,
    logout,

    // First time visitor
    isFirstTime,
    markAsReturningUser,
    resetFirstTimeStatus,

    // Cart
    cart,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    isLoadingCart,

    // Wishlist
    wishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    isLoadingWishlist,

    // Sidebar
    isSidebarOpen,
    toggleSidebar,
    closeSidebar,

    // Notifications
    notifications,
    addNotification,
    removeNotification,
    clearNotifications
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
